import { useState, type DragEvent } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import {
  STORY_BUTTON_MAX,
  STORY_DURATION_MAX,
  STORY_DURATION_MIN,
  STORY_SLIDES_MAX,
} from '@dorixona/api/stories'
import { Icon } from '../../ui/Icon'
import { IMAGE_ACCEPT, dragStyle, hasButton, pickFiles, slideError, totalSeconds } from './model'
import { StoryLink } from './StoryLink'
import type { StorySlide } from './api'

/** Слайд черновика: `uid` нужен перетаскиванию — у нового слайда ещё нет id. */
export type DraftSlide = StorySlide & { uid: string }

interface TileProps {
  slide: DraftSlide
  index: number
  count: number
  selected: boolean
  draggable: boolean
  invalid: boolean
  onSelect: () => void
}

function SlideTile({ slide, index, count, selected, draggable, invalid, onSelect }: TileProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: slide.uid,
    disabled: !draggable,
  })
  const style = dragStyle(transform, transition, 'x')

  return (
    <li ref={setNodeRef} style={style} className="sto-tile" data-dragging={isDragging || undefined}>
      <button
        type="button"
        className="sto-tile-img"
        aria-pressed={selected}
        aria-label={`Слайд ${index + 1} из ${count}`}
        data-invalid={invalid || undefined}
        onClick={onSelect}
      >
        <img src={slide.url} alt="" />
        <span className="sto-tile-n" aria-hidden="true">
          {index + 1}
        </span>
        {invalid ? (
          <span className="sto-tile-warn" aria-hidden="true">
            <Icon name="i-alert" size={14} />
          </span>
        ) : null}
      </button>
      {draggable ? (
        <button
          type="button"
          className="ui-iconbtn sto-tile-grip"
          aria-label={`Перетащить слайд ${index + 1}`}
          {...attributes}
          {...listeners}
        >
          <Icon name="i-grip" />
        </button>
      ) : null}
    </li>
  )
}

export interface StorySlidesProps {
  slides: DraftSlide[]
  canUpdate: boolean
  /** Сколько файлов сейчас загружается — плитки-заглушки в ленте. */
  uploading: number
  onUpload: (files: File[]) => void
  onChange: (slides: DraftSlide[]) => void
}

/**
 * Слайды сторис: лента в порядке показа и настройки выбранного. Картинки
 * перетаскивают на блок или выбирают, сразу несколько; порядок меняют
 * перетаскиванием плиток. Всё это — черновик до кнопки «Сохранить».
 */
export function StorySlides({
  slides,
  canUpdate,
  uploading,
  onUpload,
  onChange,
}: StorySlidesProps) {
  const [selectedUid, setSelectedUid] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [depth, setDepth] = useState(0)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const room = STORY_SLIDES_MAX - slides.length - uploading
  const index = Math.max(
    0,
    slides.findIndex((s) => s.uid === selectedUid),
  )
  const selected = slides[index]

  function take(files: File[]) {
    if (files.length === 0) return
    const { ok, error: reason } = pickFiles(files, room)
    setError(reason)
    if (ok.length > 0) onUpload(ok)
  }

  function patch(change: Partial<StorySlide>) {
    onChange(slides.map((s, i) => (i === index ? { ...s, ...change } : s)))
  }

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return
    const from = slides.findIndex((s) => s.uid === active.id)
    const to = slides.findIndex((s) => s.uid === over.id)
    onChange(arrayMove(slides, from, to))
  }

  const dropZone =
    canUpdate && room > 0
      ? {
          onDragEnter: (event: DragEvent) => {
            event.preventDefault()
            setDepth((d) => d + 1)
          },
          onDragOver: (event: DragEvent) => {
            event.preventDefault()
          },
          onDragLeave: () => {
            setDepth((d) => d - 1)
          },
          onDrop: (event: DragEvent) => {
            event.preventDefault()
            setDepth(0)
            take([...event.dataTransfer.files])
          },
        }
      : {}

  const picker = (
    <input
      type="file"
      className="visually-hidden"
      accept={IMAGE_ACCEPT}
      multiple
      aria-label="Добавить слайды"
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? 'sto-slides-error' : undefined}
      onChange={(e) => {
        // У поля выбора файла список есть всегда, хоть и пустой.
        const files = [...(e.target.files as FileList)]
        e.target.value = ''
        take(files)
      }}
    />
  )

  const seconds = totalSeconds(slides)
  const problem = selected ? slideError(selected) : ''

  return (
    <section
      className="ui-card ui-drop-card"
      aria-labelledby="sto-slides"
      aria-busy={uploading > 0}
      data-drop={depth > 0 || undefined}
      {...dropZone}
    >
      <div className="ui-card-head">
        <h3 id="sto-slides">Слайды</h3>
        <span className="aside">
          {uploading > 0
            ? 'Загружаем…'
            : `${slides.length} из ${STORY_SLIDES_MAX}${seconds ? ` · ${seconds} с` : ''}`}
        </span>
      </div>
      <div className="ui-card-body sto-slides-body">
        {slides.length === 0 && uploading === 0 ? (
          canUpdate ? (
            <label className="ui-drop">
              <Icon name="i-img" />
              <b>Перетащите картинки слайдов сюда</b>
              <span>
                или <u>выберите файлы</u> · JPEG, PNG, WebP до 5 МБ · 9:16, 1080×1920
              </span>
              <span>Текст и кнопки держите по центру: на узких телефонах края обрезаются</span>
              {picker}
            </label>
          ) : (
            <p className="ui-muted-line">Слайдов нет — покупатель сторис не увидит</p>
          )
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext
              items={slides.map((s) => s.uid)}
              strategy={horizontalListSortingStrategy}
            >
              <ol className="sto-strip" aria-label="Порядок слайдов">
                {slides.map((slide, i) => (
                  <SlideTile
                    key={slide.uid}
                    slide={slide}
                    index={i}
                    count={slides.length}
                    selected={i === index}
                    draggable={canUpdate && slides.length > 1}
                    invalid={slideError(slide) !== ''}
                    onSelect={() => {
                      setSelectedUid(slide.uid)
                    }}
                  />
                ))}
                {Array.from({ length: uploading }, (_, i) => (
                  <li key={`up-${i}`} className="sto-tile">
                    <span className="sto-tile-img ui-skeleton" aria-hidden="true" />
                  </li>
                ))}
                {canUpdate && room > 0 ? (
                  <li className="sto-tile">
                    <label className="ui-drop ui-drop-tile sto-tile-add">
                      <Icon name="i-plus" />
                      <span>Добавить</span>
                      {picker}
                    </label>
                  </li>
                ) : null}
              </ol>
            </SortableContext>
          </DndContext>
        )}

        {error ? (
          <span className="ui-error" id="sto-slides-error" role="alert">
            <Icon name="i-alert" />
            {error}
          </span>
        ) : null}

        {selected ? (
          <div className="sto-slide-edit" aria-label={`Настройки слайда ${index + 1}`} role="group">
            <div className="sto-slide-big">
              <img src={selected.url} alt={`Слайд ${index + 1}`} />
            </div>
            <div className="ui-form">
              <div className="ui-field">
                <label className="ui-label" htmlFor="sto-duration">
                  Длительность
                </label>
                <div className="ui-unit">
                  <input
                    id="sto-duration"
                    className="ui-input"
                    type="number"
                    inputMode="numeric"
                    min={STORY_DURATION_MIN}
                    max={STORY_DURATION_MAX}
                    disabled={!canUpdate}
                    aria-describedby="sto-duration-hint"
                    value={selected.duration}
                    onChange={(e) => {
                      patch({ duration: Number(e.target.value) })
                    }}
                    onBlur={() => {
                      patch({
                        duration: Math.min(
                          STORY_DURATION_MAX,
                          Math.max(STORY_DURATION_MIN, Math.round(selected.duration || 0)),
                        ),
                      })
                    }}
                  />
                  <span aria-hidden="true">с</span>
                </div>
                <span className="ui-hint" id="sto-duration-hint">
                  {STORY_DURATION_MIN}–{STORY_DURATION_MAX} секунд
                </span>
              </div>
              <div className="ui-form-row">
                <div className="ui-field">
                  <label className="ui-label" htmlFor="sto-btn-ru">
                    Кнопка на русском
                  </label>
                  <input
                    id="sto-btn-ru"
                    placeholder="Например, Подробнее"
                    className="ui-input"
                    lang="ru"
                    maxLength={STORY_BUTTON_MAX}
                    disabled={!canUpdate}
                    value={selected.buttonRu}
                    onChange={(e) => {
                      patch({ buttonRu: e.target.value })
                    }}
                  />
                </div>
                <div className="ui-field">
                  <label className="ui-label" htmlFor="sto-btn-uz">
                    Кнопка на узбекском
                  </label>
                  <input
                    id="sto-btn-uz"
                    placeholder="Masalan, Batafsil"
                    className="ui-input"
                    lang="uz"
                    maxLength={STORY_BUTTON_MAX}
                    disabled={!canUpdate}
                    value={selected.buttonUz}
                    onChange={(e) => {
                      patch({ buttonUz: e.target.value })
                    }}
                  />
                </div>
              </div>
              {hasButton(selected) ? (
                <StoryLink
                  key={selected.uid}
                  link={selected.link}
                  disabled={!canUpdate}
                  error={problem}
                  onChange={(link) => {
                    patch({ link })
                  }}
                />
              ) : (
                <span className="ui-hint">Подпись пустая — слайд без кнопки</span>
              )}
              {canUpdate ? (
                <div>
                  <button
                    type="button"
                    className="ui-btn"
                    data-variant="secondary"
                    data-size="sm"
                    onClick={() => {
                      onChange(slides.filter((_, i) => i !== index))
                      setSelectedUid(slides[index + 1]?.uid ?? slides[index - 1]?.uid ?? null)
                    }}
                  >
                    <Icon name="i-trash" />
                    Убрать слайд
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {depth > 0 ? (
        <div className="ui-drop-over" aria-hidden="true">
          <Icon name="i-img" />
          Отпустите, чтобы добавить слайды
        </div>
      ) : null}
    </section>
  )
}
